import { Input } from '@fluentui/react-components';
import { useLoadScript, type Libraries } from '@react-google-maps/api';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { extractLatLng, UNABLE_TO_FIND_LOCATION } from '@moko/utils';
import { useOtherInfoContext } from '../OtherInfoContext';
import { useDebouncedCallback } from '../../hooks/useDebouncedCallback';
import { useOfflineStatus } from '../../hooks/useOfflineStatus';
import { isFieldReadOnly } from '../fieldReadOnly';
import { resolveFieldValue } from '../useFieldState';
import { FieldWrapper } from './FieldWrapper';
import type { FieldComponentProps } from './types';
import { addressJurisdiction } from './addressJurisdiction';

const locationName = (fieldName: string) => `${fieldName}_Location`;

const isUnableToFindLocation = (value: unknown): boolean =>
  Boolean(
    value &&
      typeof value === 'object' &&
      !Array.isArray(value) &&
      (value as { unableToFind?: unknown }).unableToFind === true
  );

const hasResolvedLocation = (value: unknown): boolean => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  if (isUnableToFindLocation(value)) return false;
  const loc = value as {
    latitude?: unknown;
    longitude?: unknown;
    countryCode?: string;
    stateCode?: string;
  };
  return (
    typeof loc.latitude === 'number' &&
    typeof loc.longitude === 'number' &&
    !!loc.countryCode &&
    (loc.countryCode !== 'US' || !!loc.stateCode)
  );
};

export const AddressField = ({
  field,
  fieldValue,
  isFieldDisabled,
  isSaved,
  validationMessage,
}: FieldComponentProps) => {
  const { t } = useTranslation();
  const {
    handleOtherInfoChange,
    otherInfoObj,
    getRepeatingPanelIndex,
    currentElementInEntityRepeatingPanel,
    params,
  } = useOtherInfoContext();
  const [inputValue, setInputValue] = useState(
    fieldValue == null ? '' : String(fieldValue)
  );
  const { isOnline, canWorkOffline } = useOfflineStatus();
  const inputRef = useRef<HTMLInputElement>(null);
  const geocodeGen = useRef(0);
  const didValidateLoaded = useRef(false);
  const libraries = useMemo<Libraries>(() => ['places'], []);
  const apiKey = import.meta.env.VITE_GOOGLE_MAP_APIKEY as string | undefined;
  const isOfflineMode = canWorkOffline && !isOnline;
  const isReadOnly = isSaved || isFieldReadOnly(field) || isOfflineMode;
  const storedLocation = resolveFieldValue(
    locationName(field.name),
    otherInfoObj,
    params.metadata.fields,
    getRepeatingPanelIndex,
    currentElementInEntityRepeatingPanel
  );
  const unableToFind = isUnableToFindLocation(storedLocation);

  const { isLoaded } = useLoadScript({
    googleMapsApiKey: apiKey ?? '',
    libraries,
  });

  const geocodeAddress = useCallback(
    (address: string) => {
      const trimmed = address.trim();
      const locField = locationName(field.name);
      if (!trimmed) {
        handleOtherInfoChange(locField, UNABLE_TO_FIND_LOCATION);
        return;
      }
      const googleMaps = (window as Window & { google?: any }).google;
      if (!googleMaps?.maps?.Geocoder) {
        handleOtherInfoChange(locField, UNABLE_TO_FIND_LOCATION);
        return;
      }
      const requestId = ++geocodeGen.current;
      const geocoder = new googleMaps.maps.Geocoder();
      geocoder.geocode({ address: trimmed }, (results: any, status: string) => {
        if (requestId !== geocodeGen.current) return;
        if (status === 'OK' && results?.[0]) {
          const coords = extractLatLng(results[0].geometry?.location);
          if (coords) {
            handleOtherInfoChange(locField, {
              ...coords,
              ...addressJurisdiction(results[0].address_components),
            });
            return;
          }
        }
        handleOtherInfoChange(locField, UNABLE_TO_FIND_LOCATION);
      });
    },
    [field.name, handleOtherInfoChange]
  );

  const geocodeDebounced = useDebouncedCallback(geocodeAddress, 400);

  useEffect(() => {
    setInputValue(fieldValue == null ? '' : String(fieldValue));
  }, [fieldValue]);

  useEffect(() => {
    if (!isLoaded || isReadOnly || isOfflineMode || didValidateLoaded.current)
      return;
    const text = (fieldValue == null ? '' : String(fieldValue)).trim();
    if (!text) return;
    didValidateLoaded.current = true;
    if (!hasResolvedLocation(storedLocation)) {
      geocodeAddress(text);
    }
  }, [
    fieldValue,
    geocodeAddress,
    isLoaded,
    isOfflineMode,
    isReadOnly,
    storedLocation,
  ]);

  useEffect(() => {
    if (
      !isLoaded ||
      !apiKey ||
      !inputRef.current ||
      isReadOnly ||
      isOfflineMode
    )
      return;

    const googleMaps = (window as Window & { google?: any }).google;
    if (!googleMaps?.maps?.places) return;

    const autocomplete = new googleMaps.maps.places.Autocomplete(
      inputRef.current,
      {
        fields: ['formatted_address', 'geometry', 'name', 'address_components'],
      }
    );

    const listener = autocomplete.addListener('place_changed', () => {
      geocodeDebounced.cancel();
      geocodeGen.current += 1;
      const place = autocomplete.getPlace();
      const formattedAddress =
        place?.formatted_address ?? inputRef.current?.value ?? '';
      setInputValue(formattedAddress);
      handleOtherInfoChange(field.name, formattedAddress);
      const coords = extractLatLng(place?.geometry?.location);
      if (coords) {
        handleOtherInfoChange(locationName(field.name), {
          ...coords,
          ...addressJurisdiction(place.address_components),
        });
        return;
      }
      geocodeAddress(formattedAddress);
    });

    return () => {
      googleMaps.maps.event.removeListener(listener);
    };
  }, [
    apiKey,
    field.name,
    geocodeAddress,
    geocodeDebounced,
    handleOtherInfoChange,
    isLoaded,
    isOfflineMode,
    isReadOnly,
  ]);

  const locationWarning =
    unableToFind && inputValue.trim()
      ? t('unableToFindTheLocation', 'Unable to find the location')
      : undefined;
  const message = validationMessage ?? locationWarning;
  const validationState = validationMessage
    ? undefined
    : locationWarning
    ? 'warning'
    : undefined;

  return (
    <FieldWrapper
      field={field}
      validationMessage={message}
      validationState={validationState}
    >
      <Input
        id={field.name}
        type="text"
        value={inputValue}
        onChange={(_, data) => {
          setInputValue(data.value);
          if (isReadOnly) return;
          handleOtherInfoChange(field.name, data.value);
          // A new address must not retain the previous address's billing jurisdiction.
          geocodeGen.current += 1;
          handleOtherInfoChange(
            locationName(field.name),
            UNABLE_TO_FIND_LOCATION
          );
          if (!data.value.trim()) {
            geocodeDebounced.cancel();
            return;
          }
          geocodeDebounced(data.value);
        }}
        readOnly={isReadOnly}
        disabled={isFieldDisabled}
        autoComplete="off"
        ref={inputRef}
      />
    </FieldWrapper>
  );
};
